<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { getDeviceSettings, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import ButtonModeOptions from './ButtonModeOptions.svelte';
	import InitialStateOptions from './InitialStateOptions.svelte';
	import IoModeAndPower from './IoModeAndPower.svelte';
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from 'src/helpers/interface_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';
	import { Button, Container, toast } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import EnableInputByIdG2 from './EnableInputByIdG2.svelte';
	import InvertInputByIdG2 from './InvertInputByIdG2.svelte';
	import LockInputsG2 from './LockInputsG2.svelte';
	import PhysicalInputContainer from './PhysicalInputContainer.svelte';
	import InputState from '../../../ApplicationSettings/InputState.svelte';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { deviceSettingsAdvanced } from 'src/helpers/settings/device_settings_advanced';
	import {
		buildIoFieldConfig,
		deviceHasInputStateSetting,
		deviceShowsInputStateSetting,
		isNonEmptyConfig,
		sendIoSavePayloads,
	} from './io_save';
	import AdvancedSettingBlock from './AdvancedSettingBlock.svelte';
	import { devices } from 'src/stores/lists/devices';
	import { compare2Versions } from 'src/helpers/firmware_compare_helpers';
	import { capFirstChar } from 'src/helpers/utilities';

	export let device: Device;

	const id = real_id(device.id);
	const componentPath = get_gen_2_component_channel_structure(device);
	// Input type is fixed to Button on these models (the local web locks it), so
	// there is no type selector here. Modes are limited to the two the firmware
	// supports: Attached (momentary) and Detached.
	const typeValue = 'button';

	const buttonModeOptions = {
		momentary: {
			type: 'button',
			text: $format('btn_mode_attached_desc', {
				default: 'One button turns the output on, the other turns it off.',
			}),
			title: $format('btn_mode_attached_title', { default: 'Attached' }),
			description: $format('btn_mode_attached_desc', {
				default: 'One button turns the output on, the other turns it off.',
			}),
		},
		detached: {
			type: 'any',
			text: $format('btn_mode_detached_description', {
				default:
					'Set Shelly device to be in "Detached" switch mode - switch is separated from the relays.',
			}),
			title: $format('btn_mode_detached_title', { default: 'Detached switch' }),
			description: $format('btn_mode_detached_desc', {
				default: 'Switch is separated from the relays.',
			}),
		},
	};

	const initialStateOptions = {
		on: {
			type: 'any',
			text: $format('init_state_on_description', {
				default: 'Configure Shelly device to Turn ON, when it has power.',
			}),
			title: $format('init_state_on_title', { default: 'Turn on' }),
			description: $format('init_state_on_desc', {
				default: 'Automatically turns the device ON when power is restored.',
			}),
		},
		off: {
			type: 'any',
			text: $format('init_state_off_description', {
				default: 'Configure Shelly device to Turn OFF, when it has power.',
			}),
			title: $format('init_state_off_title', { default: 'Turn off' }),
			description: $format('init_state_off_desc', {
				default: 'Keeps the device OFF when power is restored.',
			}),
		},
		restore_last: {
			type: 'any',
			text: $format('init_state_report_last_description', {
				default:
					'Configure Shelly device to Restore the last mode it was in, when it has power.',
			}),
			title: $format('init_state_restore_last_title', { default: 'Restore last state' }),
			description: $format('init_state_restore_last_desc', {
				default: 'Returns the device to its previous state when power is restored.',
			}),
		},
	};

	let settings = readDeviceSettings(id);
	let modeValue = settings[componentPath]?.in_mode;
	let cacheModeValue = modeValue;
	let initialStateValue = settings[componentPath]?.initial_state;
	let cacheInitialStateValue = initialStateValue;
	let loading = false;

	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	$: reactiveStateValue = initialStateValue;

	const sentRequests = async () => {
		if (cacheInitialStateValue === initialStateValue && cacheModeValue === modeValue) {
			return;
		}
		loading = true;
		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			{
				id: device.channel,
				config: {
					in_mode: modeValue,
					initial_state: initialStateValue,
				},
			},
		);

		if (res.isok) {
			if (cacheModeValue !== modeValue) {
				toast.open({
					type: 'success',
					title: $format('btn_mode_scc_toast', {
						values: { mode_value: modeValue },
						default: 'Button mode set to {mode_value}.',
					}),
				});
				cacheModeValue = modeValue;
				if (settings[componentPath]) {
					settings[componentPath].in_mode = modeValue;
					devices_settings.update_devices_settings(id, settings);
				}
			}
			if (cacheInitialStateValue !== initialStateValue) {
				toast.open({
					type: 'success',
					title: $format('init_state_scc_toast', {
						values: { initial_state_value: $format(initialStateValue) },
						default: 'Initial state set to {initial_state_value}.',
					}),
				});
				cacheInitialStateValue = initialStateValue;
				if (settings[componentPath]) {
					settings[componentPath].initial_state = initialStateValue;
					devices_settings.update_devices_settings(id, settings);
				}
			}
		} else {
			if (cacheModeValue !== modeValue) {
				modeValue = cacheModeValue;
				toast.open({
					type: 'danger',
					title: $format('btn_mode_err_toast', {
						default: 'Error! Could not change button mode.',
					}),
				});
			}
			if (cacheInitialStateValue !== initialStateValue) {
				initialStateValue = cacheInitialStateValue;
				toast.open({
					type: 'danger',
					title: $format('init_state_err_toast', {
						default: 'Could not change input initial state.',
					}),
				});
			}
		}
		loading = false;
	};

	const firmWareVersion = settings?.sys?.device.fw_id || settings?.DeviceInfo?.fw_id;
	let enable0 = settings[`input:0`]?.enable ?? false;
	let cacheEnable0 = enable0;
	let enable1 = settings[`input:1`]?.enable ?? false;
	let cacheEnable1 = enable1;
	let lockValue = settings[componentPath]?.in_locked ?? false;
	let cacheLock = lockValue;
	let invert0 = settings[`input:0`]?.invert ?? false;
	let cacheInvert0 = invert0;
	let invert1 = settings[`input:1`]?.invert ?? false;
	let cacheInvert1 = invert1;
	let inputStateValue = $devices[device.id]?.input_state || false;
	let cacheInputState = inputStateValue;

	$: showEnableInput =
		(settings[`input:0`]?.enable !== undefined || settings[`input:1`]?.enable !== undefined) &&
		compare2Versions(firmWareVersion, '1.0.7') !== 1;
	$: showLockInputs =
		settings[componentPath]?.in_locked !== undefined &&
		compare2Versions(firmWareVersion, '1.6.0') !== 1;
	$: showInvertSwitch =
		settings[`input:0`]?.invert !== undefined || settings[`input:1`]?.invert !== undefined;
	$: hasInputStateSetting = deviceHasInputStateSetting(device);
	$: showInputState = deviceShowsInputStateSetting(device, $deviceSettingsAdvanced);
	$: showPhysicalInput =
		$showNewLayout && (showEnableInput || showLockInputs || showInvertSwitch || showInputState);

	const saveAll = async () => {
		loading = true;
		const input0 = buildIoFieldConfig(
			{
				...(settings[`input:0`]?.enable !== undefined ? { enable: enable0 } : {}),
				...(settings[`input:0`]?.invert !== undefined ? { invert: invert0 } : {}),
			},
			{ enable: cacheEnable0, invert: cacheInvert0 },
		);
		const input1 = buildIoFieldConfig(
			{
				...(settings[`input:1`]?.enable !== undefined ? { enable: enable1 } : {}),
				...(settings[`input:1`]?.invert !== undefined ? { invert: invert1 } : {}),
			},
			{ enable: cacheEnable1, invert: cacheInvert1 },
		);
		const controlConfig = buildIoFieldConfig(
			{
				in_mode: modeValue,
				initial_state: initialStateValue,
				...(showLockInputs ? { in_locked: lockValue } : {}),
			},
			{
				in_mode: cacheModeValue,
				initial_state: cacheInitialStateValue,
				in_locked: cacheLock,
			},
		);
		const inputUpdates = [];
		if (isNonEmptyConfig(input0)) inputUpdates.push({ id: 0, config: input0 });
		if (isNonEmptyConfig(input1)) inputUpdates.push({ id: 1, config: input1 });
		const { ok } = await sendIoSavePayloads({
			device,
			inputUpdates,
			controlUpdates: isNonEmptyConfig(controlConfig)
				? [
						{
							id: device.channel,
							method: `${get_gen_2_component_from_category(device)}.SetConfig`,
							config: controlConfig,
						},
					]
				: [],
			inputState: showInputState ? inputStateValue : undefined,
			cachedInputState: cacheInputState,
		});
		if (ok) {
			if (input0.enable !== undefined) {
				settings[`input:0`].enable = enable0;
				cacheEnable0 = enable0;
			}
			if (input0.invert !== undefined) {
				settings[`input:0`].invert = invert0;
				cacheInvert0 = invert0;
			}
			if (input1.enable !== undefined) {
				settings[`input:1`].enable = enable1;
				cacheEnable1 = enable1;
			}
			if (input1.invert !== undefined) {
				settings[`input:1`].invert = invert1;
				cacheInvert1 = invert1;
			}
			if (controlConfig.in_mode !== undefined) {
				cacheModeValue = modeValue;
				if (settings[componentPath]) settings[componentPath].in_mode = modeValue;
			}
			if (controlConfig.initial_state !== undefined) {
				cacheInitialStateValue = initialStateValue;
				if (settings[componentPath]) settings[componentPath].initial_state = initialStateValue;
			}
			if (controlConfig.in_locked !== undefined) {
				cacheLock = lockValue;
				if (settings[componentPath]) settings[componentPath].in_locked = lockValue;
			}
			if (showInputState) cacheInputState = inputStateValue;
			devices_settings.update_devices_settings(id, settings);
		}
		loading = false;
	};

	$: isDirty =
		cacheModeValue !== modeValue ||
		cacheInitialStateValue !== initialStateValue ||
		enable0 !== cacheEnable0 ||
		enable1 !== cacheEnable1 ||
		lockValue !== cacheLock ||
		invert0 !== cacheInvert0 ||
		invert1 !== cacheInvert1 ||
		(showInputState && inputStateValue !== cacheInputState);

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: saveAll,
				loading,
				disabled: loading || !isDirty,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	};
</script>

{#if $showNewLayout}
	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<IoModeAndPower>
			<ButtonModeOptions
				{typeValue}
				bind:modeValue
				{buttonModeOptions}
				_modeValue={modeValue}
				bind:reactiveStateValue
			/>
			<InitialStateOptions
				{typeValue}
				bind:initialStateValue
				mandatoryUpdate={false}
				{initialStateOptions}
				{modeValue}
			/>
		</IoModeAndPower>
	</Container>
	<PhysicalInputContainer visible={showPhysicalInput}>
			{#if showEnableInput}
				<div class="mb-m">
					<EnableInputByIdG2
						{device}
						inputId={0}
						deferSave
						bind:value={enable0}
						title={$format('sub_settings_title_enable_input_0', { default: 'Enable input 0' })}
					/>
				</div>
				<div class="mb-m">
					<EnableInputByIdG2
						{device}
						inputId={1}
						deferSave
						bind:value={enable1}
						title={$format('sub_settings_title_enable_input_1', { default: 'Enable input 1' })}
					/>
				</div>
			{/if}
			{#if showLockInputs}
				<div class="mb-m">
					<LockInputsG2
						{device}
						deferSave
						bind:value={lockValue}
						title={$format('sub_settings_title_lock_inputs', { default: 'Lock inputs' })}
					/>
				</div>
			{/if}
			{#if showInvertSwitch}
				<div class="mb-m">
					<InvertInputByIdG2
						{device}
						inputId={0}
						deferSave
						bind:value={invert0}
						title={$format('sub_settings_title_invert_input_0', { default: 'Invert input 0' })}
					/>
				</div>
				<div class="mb-m">
					<InvertInputByIdG2
						{device}
						inputId={1}
						deferSave
						bind:value={invert1}
						title={$format('sub_settings_title_invert_input_1', { default: 'Invert input 1' })}
					/>
				</div>
			{/if}
			{#if hasInputStateSetting}
				<AdvancedSettingBlock collapsed={!showInputState}>
					<InputState
						{device}
						deferSave
						bind:value={inputStateValue}
						title={capFirstChar($format('sub_settings_title_input_state', { default: 'Input state' }))}
					/>
				</AdvancedSettingBlock>
			{/if}
	</PhysicalInputContainer>
{:else}
	<ButtonModeOptions
		{typeValue}
		bind:modeValue
		{buttonModeOptions}
		_modeValue={modeValue}
		bind:reactiveStateValue
	/>
	<InitialStateOptions
		{typeValue}
		bind:initialStateValue
		mandatoryUpdate={false}
		{initialStateOptions}
		{modeValue}
	/>
	<Button
		class="mt-s"
		{loading}
		size="m"
		width="full"
		on:click={sentRequests}
		aria-label={$format('button_save', { default: 'Save' })}
	>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
